import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, getErrorMessage } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";

interface AddByNameDialogProps {
  listId: string;
  open: boolean;
  onClose: () => void;
}

/** Put a company the database does not hold on the list, to link later. */
export default function AddByNameDialog({ listId, open, onClose }: AddByNameDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {open && <AddByNameForm listId={listId} onClose={onClose} />}
    </Dialog>
  );
}

function AddByNameForm({ listId, onClose }: { listId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [orgNumber, setOrgNumber] = useState("");
  const [website, setWebsite] = useState("");

  const add = useMutation({
    mutationFn: () =>
      api.addUnresolvedProspect(listId, {
        name: name.trim(),
        country: country.trim() || null,
        org_number: orgNumber.trim() || null,
        website: website.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.prospectList.detail(listId) });
      onClose();
    },
  });

  return (
    <>
      <DialogTitle>Add a company by name</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            For a company the database does not hold. If its org number or website finds one,
            that company is added instead; otherwise it shows as "Not in database" until someone
            links it.
          </Typography>
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="The organisation's own name, e.g. Randers Kommune"
            autoFocus
            required
          />
          <TextField
            label="Country"
            size="small"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            helperText="Two-letter code, e.g. DK"
          />
          <TextField
            label="Org number"
            size="small"
            value={orgNumber}
            onChange={(e) => setOrgNumber(e.target.value)}
          />
          <TextField
            label="Website"
            size="small"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          {add.isError && <Alert severity="error">{getErrorMessage(add.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => add.mutate()}
          disabled={!name.trim() || add.isPending}
        >
          Add
        </Button>
      </DialogActions>
    </>
  );
}
